import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useDispatch } from 'react-redux';

import './index.scss';
import { Badge, CustomTabs, Text } from '../../../shared/uiComponents';
import { ChevronRightIcon, CopyIcon } from '../../../shared/Assets/svg';
import { useCheckPermission, useScreenWidth } from '../../../shared/Helpers/hooks';
import { BadgeTypes, ClientStatusTypes } from '../../../shared/uiComponents/StatusBadge';
import { TEXT } from '../../../shared/Helpers/colors';

import { PERMISSIONS } from '../../../App/constants';
import { AdminTypes } from '../../../components/Menu';
import SkillAcquisition from './SkillAcquisition';
import BehaviorReduction from './BehaviorReduction';
import MasteredTargets from './MasteredTargets';
import InsuranceAuthorization from './InsuranceAuthorization';
import TherapistsList from './therapistsList';
import ClientInfo from './clientInfo';
import DeleteClient from './deleteClient';
import MobileClientTabs from './mobileClientTabs';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { getClient } from '../../../redux/State/clientSlice/clientSlice';
import { setClientId, setSearchStringClient } from '../../../redux/State/clientSlice/billingSlice';
import { getBranchBadgeType } from '../../../shared/Helpers/constants';

const Client = () => {
	const screenWidth = useScreenWidth();
	const isMobile = screenWidth <= 768;

	const dispatch = useDispatch<DispatchProperties>();
	const navigate = useNavigate();
	const { clientId } = useParams();
	const INSURANCE_AUTHORIZATION = useCheckPermission(PERMISSIONS.INSURANCE_AUTHORIZATION.READ);
	const DELETE = useCheckPermission(PERMISSIONS.CLIENT.DELETE);

	const client = useSelector((state) => state.client.client);
	const role = useSelector((state) => state.account.role);
	const isBT = role?.section.id === AdminTypes.bt;

	const [activeTab, setActiveTab] = useState<number>(0);

	const accordionHeaders = isBT
		? ['Skill Acquisition', 'Behavior Reduction']
		: ['Skill Acquisition', 'Behavior Reduction', 'Maintenance'];

	const accordionPanels = isBT
		? [SkillAcquisition, BehaviorReduction]
		: [SkillAcquisition, BehaviorReduction, MasteredTargets];

	const clientName = client.fullName;
	const status = client.status;
	const branches = client.branches;

	useEffect(() => {
		if (!clientId) return;
		dispatch(getClient(clientId));
	}, [clientId, dispatch]);

	const navigateToClientsSOAPNotes = () => {
		if (!clientId) return;
		navigate('/soap-notes');
		dispatch(setClientId(clientId));
		dispatch(setSearchStringClient(clientName));
	};

	const renderSoapNotes = () => (
		<div className="soapNoteNavigator" onClick={navigateToClientsSOAPNotes}>
			<div className="soapNoteNavigatorLeft">
				<CopyIcon />
				<Text title="Recent SOAP Notes" type="pmd" fontWeight="semiBold" color={TEXT.SECONDARY} />
			</div>
			<ChevronRightIcon />
		</div>
	);

	const renderHeader = () => (
		<div className="clientInfoHeader">
			<div className="clientInfoHeaderLeft">
				<Text title={clientName} fontWeight="semiBold" font="header" type="h5" />
				<div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
					<Badge label={status.name} sizeType="md" type={ClientStatusTypes[status.id] as BadgeTypes} />
					{branches && branches.length > 0 && (
						<>
							{branches.map((branch) => (
								<Badge key={branch.id} label={branch.name} type={getBranchBadgeType(branch.state)} sizeType="md" />
							))}
						</>
					)}
				</div>
			</div>
			<div className="clientsActionWrapperItem">{DELETE.permissionGranted && <DeleteClient />}</div>
		</div>
	);

	if (isMobile) {
		return (
			<div className="clientInfoContainer">
				{renderHeader()}
				{renderSoapNotes()}
				<ClientInfo />
				<div className="clientInfoMobileTabsContainer">
					<div className="clientInfoMobileTabsHeader">
						<MobileClientTabs headers={accordionHeaders} activeTab={activeTab} onTabChange={setActiveTab} />
						<Text title={accordionHeaders[activeTab]} type="pmd" fontWeight="semiBold" color={TEXT.PRIMARY} />
						{activeTab === 0 && (
							<>
								<SkillAcquisition mobileUploadButton />
								<SkillAcquisition mobileHeaderButton />
							</>
						)}
						{activeTab === 1 && <BehaviorReduction mobileHeaderButton />}
					</div>
					<div className="clientInfoMobileTabsContent">
						{activeTab === 0 && <SkillAcquisition />}
						{activeTab === 1 && <BehaviorReduction />}
						{activeTab === 2 && <MasteredTargets />}
					</div>
				</div>
				<TherapistsList />
				{INSURANCE_AUTHORIZATION.permissionGranted && <InsuranceAuthorization />}
			</div>
		);
	}

	return (
		<div className="clientInfoContainer">
			{renderHeader()}
			<div className="clientInfoDesktopLayout">
				<div className="clientInfoDesktopLayoutLeft">
					<div className="clientInfoDesktopCard">
						<CustomTabs headers={accordionHeaders} panels={accordionPanels} className="clientInfoTabs marginBottom16" />
					</div>
					{!isBT && (
						<div className="clientInfoDesktopCard">
							<TherapistsList />
						</div>
					)}
					{INSURANCE_AUTHORIZATION.permissionGranted && (
						<div className="clientInfoDesktopCard">
							<InsuranceAuthorization />
						</div>
					)}
				</div>
				<div className="clientInfoDesktopLayoutRight">
					{renderSoapNotes()}
					<ClientInfo />
				</div>
			</div>
		</div>
	);
};

export default Client;
